 
import React, { useState, useEffect } from 'react'; // Add useEffect import
import { Grid, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  TextField,
  Typography,
} from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { truncateString } from '../../../core-utils/helper-utils/riskPatientDetailsHelper';
import EditIconSvg from '../../atoms/Icons/EditIconSvg';
import { patientAccessTheme } from '@HomaHealth/ui-component-library';
import BasicModal from '../../atoms/BasicModal';
import axios from 'axios';
import { useStores } from '../../../stores';
import properties from '../../../core-utils/properties';
import useSnackbar from '../../../core-utils/custom-hooks/Snackbar';
import ReportCreateNewLabelField from '../../atoms/ReportCreateNewLabelField';
import AutocompleteWithCheckBoxes from '../../atoms/Autocomplete';
import Dropdown from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Dropdown';
import { monthOptions } from '../../../core-utils/helper-utils/experienceHelper';
import Snackbar from '../../atoms/Snackbar';
import { TypableDropdown } from '@HomaHealth/ui-component-library/lib/_AIQ-components';

interface OrganisationInfoProps {
  groupedData: any[];
  setIsEditOrg: (val: boolean) => void;
  organisationData: any;
  isEditOrg: boolean;
  getOrganizationInfo: () => Promise<void>;
  getAllInsights: () => Promise<void>;
}

const useStyles = makeStyles()({
  headerRow: {
    display: 'flex',
    width: '100%',
    backgroundColor: '#FFFFFF',
    height: '98px',
  },
  detailsContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    padding: '24px 0px 24px 48px',
    width: '98%',
    gap: '16px',
  },
  itemContainer: {
    display: 'flex',
    gap: '30px',
  },
  organisationItem: {
    flex: '1 0 0',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  organisationLabel: {
    fontSize: '12px',
    color: patientAccessTheme.palette.text.mediumemphasis,
    marginBottom: '4px',
  },
  editIconContainer: {
    marginLeft: 'auto',
    width: '20px',
    height: '20px',
    margin: '10px',
    cursor: 'pointer',
  },
});

const US_STATES = [
  'Alabama',
  'Alaska',
  'Arizona',
  'Arkansas',
  'California',
  'Colorado',
  'Connecticut',
  'Delaware',
  'Florida',
  'Georgia',
  'Hawaii',
  'Idaho',
  'Illinois',
  'Indiana',
  'Iowa',
  'Kansas',
  'Kentucky',
  'Louisiana',
  'Maine',
  'Maryland',
  'Massachusetts',
  'Michigan',
  'Minnesota',
  'Mississippi',
  'Missouri',
  'Montana',
  'Nebraska',
  'Nevada',
  'New Hampshire',
  'New Jersey',
  'New Mexico',
  'New York',
  'North Carolina',
  'North Dakota',
  'Ohio',
  'Oklahoma',
  'Oregon',
  'Pennsylvania',
  'Rhode Island',
  'South Carolina',
  'South Dakota',
  'Tennessee',
  'Texas',
  'Utah',
  'Vermont',
  'Virginia',
  'Washington',
  'West Virginia',
  'Wisconsin',
  'Wyoming',
];

const OrganisationInfo: React.FC<OrganisationInfoProps> = ({
  groupedData,
  organisationData,
  setIsEditOrg,
  isEditOrg,
  getOrganizationInfo,
  getAllInsights,
}) => {
  const { classes } = useStyles();
  const employerGroupStore = useStores().employerGroupStore;
  const egNid = employerGroupStore.employerGroupResponseData.eg_nid[0];
  const [editOrganisationData, setEditOrganisationData] = useState(
    organisationData,
  );
  const { snackbarState, openSnackbar, handleCloseSnackBar } = useSnackbar();
  useEffect(() => {
    setEditOrganisationData(organisationData);
  }, [organisationData]);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setEditOrganisationData((prev: any) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveOrganisationDetails = async () => {
    try {
      const payload = {
        ...editOrganisationData,
        eg_nid: egNid,
      };
      const response = await axios.post(
        properties.aiqBaseUrl +
          properties.contractBaseUrl +
          properties.updateOrganisationDetails,
        payload,
      );

      if (response?.data?.status === 'success') {
        openSnackbar(
          'Organization details updated successfully',
          'success',
          3000,
        );
        setIsEditOrg(false);
        getOrganizationInfo();
        getAllInsights();
      } else {
        openSnackbar('Update failed. Please try again.', 'error', 3000);
      }
    } catch (error) {
      openSnackbar('Error Saving Organization Information', 'error', 3000);
    }
  };

  return (
    <Grid className={classes.headerRow}>
      <Grid className={classes.detailsContainer}>
        {groupedData?.map((group, groupIndex) => (
          <React.Fragment key={groupIndex}>
            {group?.map((item: any, index: number) => (
              <Grid
                key={`${groupIndex}-${index}`}
                className={classes.organisationItem}
              >
                <Typography
                  fontSize="11px"
                  textColor={`${patientAccessTheme.palette.text.mediumemphasis}`}
                  className={classes.organisationLabel}
                >
                  {item?.label}
                </Typography>
                <Tooltip
                  disableFocusListener
                  placement="bottom-start"
                  title={<Typography fontSize="12px">{item?.value}</Typography>}
                  arrow
                >
                  <Grid>
                    <Typography
                      fontSize="14px"
                      fontType="medium"
                      noWrap
                      styleProps={{
                        whiteSpace: 'nowrap',
                        width: '133px',
                      }}
                    >
                      {truncateString(item?.value, 60)}
                    </Typography>
                  </Grid>
                </Tooltip>
              </Grid>
            ))}
            {groupIndex !== groupedData.length - 1 && (
              <Grid
                style={{
                  width: '1px',
                  backgroundColor: patientAccessTheme.palette.grey[300],
                  margin: '0 6px',
                }}
              />
            )}
          </React.Fragment>
        ))}
      </Grid>
      <Grid className={classes.editIconContainer}>
        <span onClick={() => setIsEditOrg(true)}>
          <EditIconSvg />
        </span>
      </Grid>
      <BasicModal
        open={isEditOrg}
        width="auto"
        handleClose={() => {
          setIsEditOrg(false);
          setEditOrganisationData(organisationData);
        }}
        title={'Edit Details'}
        isSaveButton={true}
        saveBtnText="Save"
        saveBtnAction={handleSaveOrganisationDetails}
      >
        <Grid className={classes.itemContainer}>
          <ReportCreateNewLabelField
            label="Employees Count"
            component={
              <TextField
                name="employee_number"
                value={editOrganisationData?.employee_number || ''}
                placeholder="Enter Employee Count"
                height="36px"
                width="301px"
                onChange={handleChange}
              />
            }
          />
          <ReportCreateNewLabelField
            label="Location"
            component={
              <AutocompleteWithCheckBoxes
                options={US_STATES}
                defaultValue={editOrganisationData?.location || []}
                handleChange={(event: any, value: any) =>
                  handleChange({ target: { name: 'location', value } })
                }
                width="301px"
                placeholder={
                  (editOrganisationData?.location?.length || 0) === 0
                    ? 'Select States'
                    : ''
                }
              />
            }
          />
          <ReportCreateNewLabelField
            label="Plan Type"
            component={
              <TypableDropdown
                name="planType"
                dropDownOptions={[
                  'Self Funded',
                  'Fully Funded',
                  'Level Funded',
                ]}
                dropDownValues={['Self Funded', 'Fully Funded', 'Level Funded']}
                placeholder="Select Funding Type"
                value={editOrganisationData.planType || ''}
                handleChange={handleChange}
                handleInputChange={handleChange}
                height="36px"
                width="301px"
                hideTextField={true}
              />
            }
          />
        </Grid>
        <Grid className={classes.itemContainer}>
          <ReportCreateNewLabelField
            label={'Renewal Month'}
            component={
              <Dropdown
                name="renewalMonth"
                label="Select Renewal Month"
                placeholder="Select Renewal Month"
                value={editOrganisationData.renewalMonth || ''}
                dropDownOptions={monthOptions}
                dropDownValues={monthOptions}
                width="301px"
                height="40px"
                handleChange={handleChange}
                selectStyles={{
                  width: '301px',
                }}
              />
            }
            marginRight="0px"
            marginBottom="0px"
          />
          <ReportCreateNewLabelField
            label="Medical Network"
            component={
              <TextField
                name="medicalNetwork"
                value={editOrganisationData.medicalNetwork || ''}
                placeholder="Enter Medical Network"
                height="36px"
                width="301px"
                onChange={handleChange}
              />
            }
          />
          <ReportCreateNewLabelField
            label="PBM"
            component={
              <TextField
                name="pbm"
                value={editOrganisationData.pbm || ''}
                placeholder="Enter PBM"
                height="36px"
                width="301px"
                onChange={handleChange}
              />
            }
          />
        </Grid>
        <Grid className={classes.itemContainer}>
          <ReportCreateNewLabelField
            label="Administrator"
            component={
              <TextField
                name="administrator"
                value={editOrganisationData.administrator || ''}
                placeholder="Enter Administrator Name"
                height="36px"
                width="301px"
                onChange={handleChange}
              />
            }
          />
          <ReportCreateNewLabelField
            label="Broker"
            component={
              <TextField
                name="broker"
                value={editOrganisationData.broker || ''}
                placeholder="Enter Broker"
                height="36px"
                width="301px"
                onChange={handleChange}
              />
            }
          />
          <ReportCreateNewLabelField
            label="Stop Loss"
            component={
              <TextField
                name="stopLoss"
                value={editOrganisationData.stopLoss || ''}
                placeholder="Enter Stop Loss"
                height="36px"
                width="301px"
                onChange={handleChange}
              />
            }
          />
        </Grid>
      </BasicModal>
      <Snackbar
        {...snackbarState}
        handleClose={handleCloseSnackBar}
        vertical="bottom"
        horizontal="right"
      />
    </Grid>
  );
};

export default OrganisationInfo;
